import React, { useState, useEffect, useRef, useCallback } from 'react';
import './FloatingWhatsApp.css';

const DEFAULT_PHONE = process.env.REACT_APP_WHATSAPP_NUMBER;
const DEFAULT_MESSAGE = process.env.REACT_APP_WHATSAPP_MESSAGE;

const FloatingWhatsApp = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const startPositionRef = useRef({ x: 0, y: 0 });
  const hasMovedRef = useRef(false);
  const iconRef = useRef(null);

  // Check login status and listen for changes
  useEffect(() => {
    const checkLoginStatus = () => {
      const loginStatus = localStorage.getItem("isLoggedIn");
      if (loginStatus === null) {
        setIsLoggedIn(false);
      } else {
        setIsLoggedIn(JSON.parse(loginStatus));
      }
    };

    // Check initial login status
    checkLoginStatus();

    // Listen for storage changes (login/logout in other tabs/windows)
    const handleStorageChange = (e) => {
      if (e.key === 'isLoggedIn') {
        checkLoginStatus();
      }
    };

    // Check login status when window gains focus (in case user logged in elsewhere)
    const handleFocus = () => {
      checkLoginStatus();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleFocus);

    // Listen for custom events (login/logout in same tab)
    const handleLoginChange = () => {
      checkLoginStatus();
    };

    window.addEventListener('linkedinAuthComplete', handleLoginChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('linkedinAuthComplete', handleLoginChange);
    };
  }, []);

  // Initialize component position and setup resize listener
  useEffect(() => {
    const savedPosition = localStorage.getItem('whatsappIconPosition');
    if (savedPosition) {
      try {
        const parsed = JSON.parse(savedPosition);
        const maxX = window.innerWidth - 50;
        const maxY = window.innerHeight - 50;
        setPosition({
          x: Math.max(0, Math.min(maxX, parsed.x)),
          y: Math.max(0, Math.min(maxY, parsed.y))
        });
      } catch (e) {
        setPosition({ x: window.innerWidth - 70, y: window.innerHeight - 70 });
      }
    } else {
      setPosition({ x: window.innerWidth - 70, y: window.innerHeight - 70 });
    }

    // Adjust icon position when window is resized to keep it within bounds
    const handleResize = () => {
      setPosition(prev => {
        const maxX = window.innerWidth - 50;
        const maxY = window.innerHeight - 50;
        return {
          x: Math.max(0, Math.min(maxX, prev.x)),
          y: Math.max(0, Math.min(maxY, prev.y))
        };
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Save position to localStorage whenever it changes
  useEffect(() => {
    if (position.x !== 0 || position.y !== 0) {
      localStorage.setItem('whatsappIconPosition', JSON.stringify(position));
    }
  }, [position]);

  // Initialize drag operation and calculate offset
  const handleStartDrag = useCallback((clientX, clientY) => {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      dragOffsetRef.current = {
        x: clientX - rect.left - rect.width / 2,
        y: clientY - rect.top - rect.height / 2
      };
      startPositionRef.current = { x: clientX, y: clientY };
      hasMovedRef.current = false;
      setIsDragging(true);
    }
  }, []);

  // Handle mouse down event to start dragging (desktop)
  const handleMouseDown = (e) => {
    e.preventDefault();
    handleStartDrag(e.clientX, e.clientY);
  };

  // Handle touch start event to start dragging (mobile/tablet)
  const handleTouchStart = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    handleStartDrag(touch.clientX, touch.clientY);
  };

  // Update icon position during drag and detect movement
  const handleMove = useCallback((clientX, clientY) => {
    const movedDistance = Math.sqrt(
      Math.pow(clientX - startPositionRef.current.x, 2) + 
      Math.pow(clientY - startPositionRef.current.y, 2)
    );
    if (movedDistance > 5) {
      hasMovedRef.current = true;
    }

    const newX = clientX - dragOffsetRef.current.x;
    const newY = clientY - dragOffsetRef.current.y;
    
    const maxX = window.innerWidth - 50;
    const maxY = window.innerHeight - 50;
    const minX = 0;
    const minY = 0;
    
    setPosition({
      x: Math.max(minX, Math.min(maxX, newX)),
      y: Math.max(minY, Math.min(maxY, newY))
    });
  }, []);

  // Handle mouse move event during drag (desktop)
  const handleMouseMove = useCallback((e) => {
    e.preventDefault();
    handleMove(e.clientX, e.clientY);
  }, [handleMove]);

  // Handle touch move event during drag (mobile/tablet)
  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    const touch = e.touches[0];
    handleMove(touch.clientX, touch.clientY);
  }, [handleMove]);

  // End drag operation and reset movement flag
  const handleEndDrag = useCallback(() => {
    setIsDragging(false);
    setTimeout(() => {
      hasMovedRef.current = false;
    }, 100);
  }, []);

  // Manage document-level drag event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleEndDrag);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleEndDrag);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleEndDrag);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleEndDrag);
      };
    }
  }, [isDragging, handleMouseMove, handleTouchMove, handleEndDrag]);

  // Open WhatsApp app on click
  const handleClick = (e) => {
    if (!hasMovedRef.current) {
      const encodedMessage = encodeURIComponent(DEFAULT_MESSAGE);
      const whatsappAppUrl = `whatsapp://send?phone=${DEFAULT_PHONE}&text=${encodedMessage}`;
      
      const link = document.createElement('a');
      link.href = whatsappAppUrl;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Don't render if user is not logged in
  if (!isLoggedIn) {
    return null;
  }

  return (
    <div
      ref={iconRef}
      className={`floating-whatsapp ${isDragging ? 'dragging' : ''}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onClick={handleClick}
      title="Click to open WhatsApp app or drag to move"
    >
      <div className="whatsapp-icon-wrapper">
        <img 
          src="/assets/images/WhatsappIcon.svg" 
          alt="WhatsApp" 
          className="whatsapp-icon-image"
        />
      </div>
    </div>
  );
};

export default FloatingWhatsApp;
